import { CalciteButton } from "@esri/calcite-components-react";
import { exclamationMarkTriangle32 } from "@esri/calcite-ui-icons";

import { ChangeEvent, MouseEvent, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { FormattedMessage } from "react-intl";
import { Link, useParams } from "react-router-dom";

import { useAuth } from "../components/AuthProvider";
import Message from "../components/Message";

function LicensePage({ portalUrl, licenseUrl }: { portalUrl: string; licenseUrl: string }) {
    const { token, isSignedIn, appAuthId, authUrlPath } = useAuth();

    const { productId, productEditorsId, productViewersId } =
            useParams<Record<string, string | undefined>>(),
        oAuthUrl: string = `${portalUrl}/oauth2/authorize?client_id=${appAuthId}&response_type=token&redirect_uri=${encodeURIComponent(
            window.location.href
        )}`;
    const [working, setWorking] = useState<boolean>(false);
    const [productPurchase, setProductPurchase] = useState<{ [id: string]: any } | null>(null);
    const [productEditorsPurchase, setProductEditorsPurchase] =
        useState<{ [id: string]: any } | null>(null);
    const [productViewersPurchase, setProductViewersPurchase] =
        useState<{ [id: string]: any } | null>(null);
    const [productDetails, setProductDetails] = useState<{ [id: string]: any } | null>(null);
    const [portalSelf, setPortalSelf] = useState<{ [id: string]: any } | null>(null);
    const [syncError, setSyncError] = useState<any>(null);
    const [sendLicReq, setSendLicReq] = useState<boolean>(false);
    const [license, setLicense] = useState<string>("");
    const [appUrls, setAppUrls] = useState<string[]>([]); // The full set
    const [url, setUrl] = useState<string>(""); // A temporary state object for use when typing...
    const [urlIndex, setUrlIndex] = useState<number>(-1);

    const { t } = useTranslation("common");

    useEffect(() => {
        async function loadProductDetails() {
            try {
                setWorking(true);
                const productSet: any = await fetch(
                    `${portalUrl}/content/listings/${productId}?f=json`
                ).then((rsp) => {
                    return rsp.json();
                });
                setProductDetails(productSet);
                if (isSignedIn) {
                    const self: { [id: string]: any } = await fetch(
                        `${portalUrl}/portals/self?f=json&token=${token}`
                    ).then((rsp) => {
                        return rsp.json();
                    });
                    if (self.error !== undefined) {
                        throw new Error(self.error.message);
                    }
                    setPortalSelf(self);
                    const bought: { [id: string]: any } = await fetch(
                            `${portalUrl}/accounts/${self.id}/purchases?f=json&status=active&token=${token}`
                        ).then((rsp) => {
                            return rsp.json();
                        }),
                        purchase: { [id: string]: any } =
                            bought.purchases !== undefined
                                ? bought.purchases.find(
                                      (p: { provision: { itemId: undefined } | undefined }) =>
                                          p.provision !== undefined &&
                                          p.provision.itemId !== undefined &&
                                          p.provision.itemId === productId
                                  )
                                : {};
                    const editorsSubPurchase: { [id: string]: any } | null =
                        bought.purchases !== undefined &&
                        productEditorsId !== undefined &&
                        productEditorsId !== "default"
                            ? bought.purchases.find(
                                  (p: { provision: { itemId: undefined } | undefined }) =>
                                      p.provision !== undefined &&
                                      p.provision.itemId !== undefined &&
                                      p.provision.itemId === productEditorsId
                              )
                            : null;
                    const viewersSubPurchase: any =
                        bought.purchases !== undefined &&
                        productViewersId !== undefined &&
                        productViewersId !== "default"
                            ? bought.purchases.find(
                                  (p: { provision: { itemId: undefined } | undefined }) =>
                                      p.provision !== undefined &&
                                      p.provision.itemId !== undefined &&
                                      p.provision.itemId === productViewersId
                              )
                            : null;
                    if (purchase === undefined) {
                        setSyncError({
                            code: "NoPurchase",
                            message: (
                                <FormattedMessage
                                    id="license.error.nopurchase.message"
                                    defaultMessage="{org} has not purchased {product}"
                                    values={{
                                        org: <strong className="org-name">{self.name}</strong>,
                                        product: (
                                            <strong className="product-name">
                                                {productSet.title}
                                            </strong>
                                        )
                                    }}
                                />
                            )
                        });
                    } else {
                        setProductPurchase(purchase);
                        setProductEditorsPurchase(editorsSubPurchase);
                        setProductViewersPurchase(viewersSubPurchase);
                    }
                }
                setWorking(false);
            } catch (syncErr) {
                setSyncError(syncErr);
                setWorking(false);
            }
        }
        loadProductDetails();
    }, [isSignedIn, portalUrl, appAuthId, productId, token, productEditorsId, productViewersId]);

    useEffect(() => {
        async function doLicenseRequest() {
            setWorking(true);
            try {
                const formData: FormData = new FormData();
                formData.append("urls", appUrls.join(",").replace(/[\n]/g, ","));
                formData.append("editorsProductId", productEditorsId || "");
                formData.append("viewersProductId", productViewersId || "");
                //formData.append('product', productId); // Part of URL
                formData.append("token", token);
                const lic: any = await fetch(
                    `${licenseUrl.replace("{product}", productId || "")}`,
                    {
                        method: "POST",
                        body: formData
                    }
                ).then((rsp) => {
                    return rsp.json();
                });
                setLicense(lic);
                setSyncError(null);
            } catch (licErr) {
                setSyncError(licErr);
            } finally {
                setWorking(false);
                setSendLicReq(false);
            }
        }
        if (sendLicReq) {
            doLicenseRequest();
        }
    }, [
        sendLicReq,
        isSignedIn,
        portalUrl,
        appAuthId,
        productId,
        token,
        licenseUrl,
        appUrls,
        productEditorsId,
        productViewersId
    ]);

    let productLabel: string = "?";
    if (productDetails !== undefined && productDetails !== null)
        productLabel = t(`products.${productDetails.itemId}`) ?? productDetails["title"];
    let productMismatch: boolean = false;
    if (portalSelf !== null && portalSelf.appInfo !== undefined)
        productMismatch = productId !== portalSelf.appInfo.itemId;
    let downloadUrl: string = "",
        downloadFile: string = "";
    if (
        productDetails !== undefined &&
        productDetails !== null &&
        license !== undefined &&
        license !== null &&
        license !== ""
    ) {
        downloadUrl = URL.createObjectURL(
            new Blob([JSON.stringify(license)], { type: "application/json" })
        );
        downloadFile = "license.json"; //`${(productDetails['title'] || 'product').toLowerCase().replace(/[^a-zA-Z0-9]/g, '-')}-lic.json`
    }

    const onUrlSelect = (e: ChangeEvent<HTMLSelectElement>) => {
        setUrlIndex(e.target.selectedIndex);
    };

    const addUrl = (e: MouseEvent<HTMLButtonElement>) => {
        setAppUrls((urls) => {
            const clones: string[] = [...urls];
            if (clones.indexOf(url) < 0) clones.push(url);
            return clones;
        });
        e.preventDefault();
    };

    const removeUrl = (e: MouseEvent<HTMLButtonElement>) => {
        setAppUrls((urls) => {
            const clones: string[] = [...urls];
            if (clones.length > urlIndex && urlIndex >= 0) clones.splice(urlIndex, 1);
            return clones;
        });
        e.preventDefault();
    };

    const helpLink =
        productId === "cd1141c867ec4d9097bcf9f824580995"
            ? "https://unehelp.une.esriuk.com/builder/#/help/deployment/arcgisent"
            : productId === "fc5a6c7852824126aa82f400e131953b"
            ? "https://www.sweet.esriuk.com/builder/#/help/deployment/arcgisent"
            : "#";

    return (
        <div className="license-page container mx-auto">
            {working && (
                <div className="masker">
                    <div className="h-48">
                        <div className="bg-white px-4 pt-12 pb-12">
                            <Message type="loading" />
                        </div>
                    </div>
                </div>
            )}
            {!isSignedIn && (
                <div className="purchase">
                    <div className="warning-message">
                        <div className="message-icon">
                            <svg
                                className="btn-icon h-6 px-2 float-left"
                                xmlns="http://www.w3.org/2000/svg"
                                viewBox="0 0 32 32"
                            >
                                <path d={exclamationMarkTriangle32} />
                            </svg>
                        </div>
                        <div className="message-body">
                            <p className="pb-4">
                                {" "}
                                <FormattedMessage
                                    id="license.warning.anonymous.message"
                                    defaultMessage="You are not signed in. Please sign in to your ArcGIS Online account. This step is required to validate your purchase of {productName}."
                                    values={{
                                        productName: (
                                            <strong className="product-name">{productLabel}</strong>
                                        )
                                    }}
                                />
                            </p>
                            <div className="btn-holder flex flex-wrap gap-2 justify-end">
                                <CalciteButton href={oAuthUrl}>
                                    <>{t("common.signin")}</>
                                </CalciteButton>
                                <CalciteButton kind="neutral" appearance="outline-fill" href={`/`}>
                                    <>{t("common.cancel")}</>
                                </CalciteButton>
                            </div>
                        </div>
                    </div>
                </div>
            )}
            {syncError !== undefined && syncError !== null && (
                <div className="warning-message">
                    {syncError.code !== undefined && syncError.code === "NoPurchase" ? (
                        <div className="purchase-thumbnail">
                            <img
                                src={`${portalUrl}/content/listings/${
                                    productDetails?.itemId
                                }/info/${
                                    productDetails?.largeThumbnail ?? productDetails?.thumbnail
                                }`}
                                alt="thumbnail"
                            />
                        </div>
                    ) : null}
                    <div className="message-icon">
                        <svg
                            className="btn-icon  h-6 px-2 float-left"
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 32 32"
                        >
                            <path d={exclamationMarkTriangle32} />
                        </svg>
                    </div>
                    <div className="message-body">
                        <p>
                            {syncError.code !== undefined && syncError.code === "NoPurchase" ? (
                                <>{syncError.message}</>
                            ) : (
                                <FormattedMessage
                                    id="license.warning.syncerror.message"
                                    defaultMessage="An unexpected error occurred: {detail}. Please try again."
                                    values={{
                                        detail: syncError.message
                                    }}
                                />
                            )}
                        </p>
                    </div>
                </div>
            )}
            {productDetails !== null && <div className="listing"></div>}
            {downloadUrl !== "" &&
                isSignedIn &&
                productDetails !== null &&
                productPurchase !== null && (
                    <div className="purchase">
                        <div className="success-message">
                            <div className="message-body">
                                <p className="mb-6">
                                    <>
                                        {t("license.form.success.instruction1", {
                                            product: productLabel
                                        })}
                                    </>
                                </p>
                                <p className="mb-6">
                                    <>{t("license.form.success.instruction2")}</>
                                </p>
                                <p className="mb-6">
                                    <>{t("license.form.success.instruction3")}</>
                                </p>
                                <div className="flex flex-wrap">
                                    <div className="mr-2">
                                        <a
                                            className="inline-flex w-full justify-center items-center border border-transparent bg-[#0079c1] px-4 py-2 text-base font-medium hover:bg-[#005e95] text-white shadow-sm focus:outline-none focus:ring-2 focus:ring-[#0079c1] focus:ring-offset-2 sm:w-auto sm:text-sm"
                                            href={downloadUrl}
                                            download={downloadFile}
                                        >
                                            <>{t("common.download")}</>
                                        </a>
                                    </div>
                                    <div>
                                        <button
                                            className="mt-3 inline-flex w-full justify-center border border-gray-300 bg-white px-4 py-2 text-base font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                                            onClick={(e) => {
                                                setLicense("");
                                                e.preventDefault();
                                            }}
                                            type="button"
                                        >
                                            <>{t("common.restart")}</>
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            {downloadUrl === "" &&
            isSignedIn &&
            productDetails !== null &&
            productPurchase !== null ? (
                <div className="purchase">
                    <p className="font-light mb-3">
                        <>{t("license.form.instructions1")}</>
                    </p>
                    <p className="font-light pb-5">
                        <>{t("license.form.instructions2a")}</>{" "}
                        <span className="font-medium">
                            {productId === "cd1141c867ec4d9097bcf9f824580995"
                                ? "https://[serverdetails]/une"
                                : productId === "fc5a6c7852824126aa82f400e131953b"
                                ? "https://[serverdetails]/sweet"
                                : "#"}
                        </span>{" "}
                        <>{t("common.or")}</>{" "}
                        <span className="font-medium">
                            {productId === "cd1141c867ec4d9097bcf9f824580995"
                                ? "https://myuneserver.com/une"
                                : productId === "fc5a6c7852824126aa82f400e131953b"
                                ? "https://mysweetserver.com/sweet"
                                : "#"}
                        </span>
                    </p>
                    <p className="font-light pb-5">
                        <>{t("license.form.instructions2b")}</>{" "}
                        <a
                            href={`/resources/${productId}?token=${token}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="underline"
                        >
                            <>{t("common.install")}</>
                        </a>{" "}
                        <>{t("license.form.instructions2c")}</>
                    </p>
                    <div className="md:grid md:grid-cols-3 md:gap-6">
                        <div className="md:mt-0 md:col-span-3">
                            <dl className="sm:divide-y sm:divide-gray-200 mb-6 border-b border-gray-200">
                                <div className="py-4 sm:grid sm:grid-cols-3 sm:gap-4 sm:py-5">
                                    <dt className="text-sm font-medium text-gray-700">
                                        <>{t("license.form.label.product")}</>
                                    </dt>
                                    <dd className="mt-1 font-light text-sm text-gray-900 sm:col-span-2 sm:mt-0">
                                        {productLabel}
                                    </dd>
                                </div>
                                <div className="py-4 sm:grid sm:grid-cols-3 sm:gap-4 sm:py-5">
                                    <dt className="text-sm font-medium text-gray-700">
                                        <>{t("license.form.label.builders")}</>
                                    </dt>
                                    <dd className="mt-1 font-light text-sm text-gray-900 sm:col-span-2 sm:mt-0">
                                        {productPurchase.provision.orgEntitlements.maxUsers}
                                    </dd>
                                </div>
                                <div
                                    className={`${
                                        productEditorsPurchase === null
                                            ? "hidden"
                                            : "py-4 sm:grid sm:grid-cols-3 sm:gap-4 sm:py-5"
                                    }`}
                                >
                                    <dt className="text-sm font-medium text-gray-700">
                                        <>{t("license.form.label.editors")}</>
                                    </dt>
                                    <dd className="mt-1 font-light text-sm text-gray-900 sm:col-span-2 sm:mt-0">
                                        {productEditorsPurchase?.provision?.orgEntitlements
                                            ?.maxUsers ?? 0}
                                    </dd>
                                </div>
                                <div
                                    className={`${
                                        productViewersPurchase === null
                                            ? "hidden"
                                            : "py-4 sm:grid sm:grid-cols-3 sm:gap-4 sm:py-5"
                                    }`}
                                >
                                    <dt className="text-sm font-medium text-gray-700">
                                        <>{t("license.form.label.viewers")}</>
                                    </dt>
                                    <dd className="mt-1 font-light text-sm text-gray-900 sm:col-span-2 sm:mt-0">
                                        {productViewersPurchase?.provision?.orgEntitlements
                                            ?.maxUsers ?? 0}
                                    </dd>
                                </div>
                            </dl>

                            <form action="#" method="POST">
                                <div className="overflow-hidden">
                                    <div>
                                        <div className="grid grid-cols-12 gap-6">
                                            <div className="col-span-12">
                                                <label
                                                    htmlFor="email_address"
                                                    className="block text-sm font-medium text-gray-700"
                                                >
                                                    <>{t("license.form.label.deploymenturls")}</>
                                                </label>

                                                <div className="flex mb-3 mt-1.5">
                                                    <input
                                                        id="url"
                                                        value={url}
                                                        type="url"
                                                        pattern="^(http|https):\/\/[a-zA-Z0-9.\-_:]+\/(.*)$"
                                                        className={`block mr-2 border border-gray-400 flex focus:border-indigo-500 focus:ring-indigo-500 h-10 items-center px-4 sm:text-sm w-full form-control ${
                                                            /^(http|https):\/\/[a-zA-Z0-9.\-_:]+\/(.*)$/.test(
                                                                url
                                                            )
                                                                ? "url-valid"
                                                                : "url-invalid"
                                                        }`}
                                                        placeholder={t(
                                                            "license.form.label.placeholder",
                                                            {
                                                                product: productLabel
                                                            }
                                                        )}
                                                        onChange={(e) => {
                                                            setUrl(e.target.value);
                                                        }}
                                                    />
                                                    <div className="flex space-x-2">
                                                        <button
                                                            className={`${
                                                                !/^(http|https):\/\/[a-zA-Z0-9.\-_:]+\/(.*)$/.test(
                                                                    url
                                                                )
                                                                    ? "opacity-50 cursor-default"
                                                                    : "hover:bg-[#005e95]"
                                                            } inline-flex w-full justify-center items-center border border-transparent bg-[#0079c1] px-4 py-2 text-base font-medium text-white shadow-sm focus:outline-none focus:ring-2 focus:ring-[#0079c1] focus:ring-offset-2 sm:w-auto sm:text-sm`}
                                                            onClick={addUrl}
                                                            disabled={
                                                                !/^(http|https):\/\/[a-zA-Z0-9.\-_:]+\/(.*)$/.test(
                                                                    url
                                                                )
                                                            }
                                                        >
                                                            <>{t("common.add")}</>
                                                        </button>
                                                        <button
                                                            className={`${
                                                                urlIndex < 0 ||
                                                                urlIndex >= appUrls.length
                                                                    ? "opacity-50 cursor-default"
                                                                    : "hover:bg-gray-50"
                                                            } mt-3 inline-flex w-full justify-center border border-gray-300 bg-white px-4 py-2 text-base font-medium text-gray-700 shadow-sm  focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm`}
                                                            onClick={removeUrl}
                                                            disabled={
                                                                urlIndex < 0 ||
                                                                urlIndex >= appUrls.length
                                                            }
                                                        >
                                                            <>{t("common.remove")}</>
                                                        </button>
                                                    </div>
                                                </div>
                                                <div>
                                                    <select
                                                        className="block border border-gray-300 focus:border-indigo-500 focus:ring-indigo-500 mt-1 px-4 py-2 shadow-sm sm:text-sm w-full"
                                                        size={5}
                                                        multiple={false}
                                                        onChange={onUrlSelect}
                                                    >
                                                        {appUrls.map((u, i) => {
                                                            return (
                                                                <option key={i} value={u}>
                                                                    {u}
                                                                </option>
                                                            );
                                                        })}
                                                    </select>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="pt-5 text-right">
                                        <button
                                            className="hover:bg-[#005e95] inline-flex w-full justify-center items-center border border-transparent bg-[#0079c1] px-4 py-2 text-base font-medium text-white shadow-sm focus:outline-none focus:ring-2 focus:ring-[#0079c1] focus:ring-offset-2 sm:w-auto sm:text-sm"
                                            type="button"
                                            onClick={(e) => {
                                                setSendLicReq(true);
                                                e.preventDefault();
                                            }}
                                            disabled={appUrls.length < 1}
                                        >
                                            <>{t("common.getlicense")}</>
                                        </button>
                                        <Link
                                            to={token ? `/${authUrlPath}` : "/"}
                                            className="mt-3 inline-flex w-full justify-center border border-gray-300 bg-white px-4 py-2 text-base font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                                        >
                                            <>{t("common.cancel")}</>
                                        </Link>
                                    </div>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            ) : null}
        </div>
    );
}

export default LicensePage;
